// Hover-triggered, in-place image zoom for the main product gallery image.
// Hovering an .image-magnify-hover image overlays a magnified crop of a
// high-res asset (data-zoom-src) that fills the same container and pans
// with the cursor; leaving the image removes the overlay and restores the
// normal fit view. Desktop-only: see the hover-capability guard below.
//
// The .product__media-toggle button sits above the image (its ::after
// covers the full frame) so it can stay the single click target that opens
// the full-screen lightbox for mouse, touch, and keyboard alike - so hover
// listeners bind to that button, not the <img>, and the zoom overlay itself
// is pointer-events: none (see section-main-product.css) so it never blocks
// that click.

const FALLBACK_ZOOM_RATIO = 2;

function isHoverZoomSupported() {
  return window.matchMedia('(hover: hover)').matches;
}

function toggleLoadingSpinner(image) {
  const loadingSpinner = image.parentElement.parentElement.querySelector(`.loading__spinner`);
  loadingSpinner.classList.toggle('hidden');
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function positionOverlay(overlay, image, event) {
  const bounds = image.getBoundingClientRect();
  const xPercent = clamp(((event.clientX - bounds.left) / bounds.width) * 100, 0, 100);
  const yPercent = clamp(((event.clientY - bounds.top) / bounds.height) * 100, 0, 100);
  overlay.style.backgroundPosition = `${xPercent}% ${yPercent}%`;
}

function createOverlay(image, event) {
  const overlay = document.createElement('div');
  overlay.setAttribute('class', 'image-magnify-full-size');
  overlay.setAttribute('aria-hidden', 'true');
  overlay.style.backgroundColor = 'var(--gradient-background)';

  image.style.opacity = '50%';
  toggleLoadingSpinner(image);

  const zoomSource = image.parentElement.dataset.zoomSrc || image.currentSrc || image.src;
  const zoomImage = new Image();

  zoomImage.onload = () => {
    toggleLoadingSpinner(image);
    image.style.opacity = '100%';

    // Fixed zoom level, derived from how much larger the high-res asset is
    // than the on-screen image - never exceeds the asset's own resolution,
    // so the magnified view doesn't render visibly pixelated.
    const ratio =
      zoomImage.naturalWidth && image.clientWidth
        ? zoomImage.naturalWidth / image.clientWidth
        : FALLBACK_ZOOM_RATIO;

    overlay.style.backgroundImage = `url('${zoomImage.src}')`;
    overlay.style.backgroundSize = `${image.clientWidth * ratio}px`;
    overlay.classList.add('image-magnify-full-size--loaded');
    positionOverlay(overlay, image, event);
  };

  zoomImage.src = zoomSource;

  image.parentElement.insertBefore(overlay, image);
  return overlay;
}

function findToggle(image) {
  return image.closest('.product__modal-opener')?.querySelector('.product__media-toggle') || null;
}

// The at-rest corner badge (.product__media-icon--hover, see
// section-main-product.css/product-thumbnail.liquid) hints "hoverable"
// before the cursor arrives, but once actually hovering it just sits fixed
// in the corner - not useful as a "you're zooming" indicator. Cloning its
// own icon into a body-level element that tracks the mouse (position:
// fixed, updated on every mousemove) puts the zoom icon where it's actually
// useful: right at the cursor. cursor: none on the toggle (see
// section-main-product.css) hides the system cursor underneath it so there
// isn't a second cursor competing for attention.
function createCursorIcon(image) {
  const badgeIcon = image.closest('.product__modal-opener')?.querySelector('.product__media-icon--hover svg');
  if (!badgeIcon) return null;

  const icon = badgeIcon.cloneNode(true);
  icon.classList.add('image-magnify-cursor-icon');
  icon.setAttribute('aria-hidden', 'true');
  document.body.appendChild(icon);
  return icon;
}

function positionCursorIcon(icon, event) {
  icon.style.left = `${event.clientX}px`;
  icon.style.top = `${event.clientY}px`;
}

function bindZoom(image) {
  const toggle = findToggle(image);
  if (!toggle) return;

  let overlay = null;
  let cursorIcon = null;

  toggle.onmouseenter = (event) => {
    if (!isHoverZoomSupported() || overlay) return;
    overlay = createOverlay(image, event);
    cursorIcon = createCursorIcon(image);
    if (cursorIcon) positionCursorIcon(cursorIcon, event);
  };

  toggle.onmousemove = (event) => {
    if (!overlay) return;
    positionOverlay(overlay, image, event);
    if (cursorIcon) positionCursorIcon(cursorIcon, event);
  };

  toggle.onmouseleave = () => {
    if (!overlay) return;
    overlay.remove();
    overlay = null;
    cursorIcon?.remove();
    cursorIcon = null;
  };
}

function enableZoomOnHover() {
  const images = document.querySelectorAll('.image-magnify-hover');

  // Desktop-only: on touch devices there is no hover to leave, so a tap
  // would open the zoom overlay and leave it stuck open, covering the image
  // and eating the touch/swipe gestures the mobile gallery slider needs
  // underneath. Binding is a no-op there (guarded again at mouseenter time
  // so it stays correct if the viewport/input changes later).
  images.forEach(bindZoom);
}

enableZoomOnHover();
